/**
 * Walk it through's record on the board: the steps the host said look right, and the one the host is on. It rides
 * inside the saved board, so it is held small here, on both sides: the reducer fits it after every change, and the
 * server refuses a board whose record does not fit. An older board may still carry the conversations the walk used to
 * keep per card (`talk`); they are ignored, and the next change to the record leaves them out.
 */
import type { BoardWalk } from "./types";

/** Why "offer" stays: it is no longer a step, but a tab still open on the old app saves it, and that save is never refused. */
const WALK_STEP_KEYS = ["page", "room", "offer", "followups"] as const;
/** Why: the whole board row is capped at 300 KB; the walk's record never takes a third of it. */
const WALK_BYTES_MAX = 96 * 1024;

function size(walk: BoardWalk): number {
  return new TextEncoder().encode(JSON.stringify(walk)).length;
}

/** The record cut to what it keeps: the step and the looked-at steps, each only a real step. */
export function fitWalk(walk: BoardWalk): BoardWalk {
  const looked = walk.looked?.filter((s, i, all) => (WALK_STEP_KEYS as readonly string[]).includes(s) && all.indexOf(s) === i);
  const step = walk.step && (WALK_STEP_KEYS as readonly string[]).includes(walk.step) ? walk.step : undefined;
  return { ...(step ? { step } : {}), ...(looked ? { looked } : {}) };
}

/** What is wrong with a saved board's walk record, in the host's words; null when nothing is. */
export function walkProblem(walk: unknown): string | null {
  if (walk === undefined) return null;
  const bad = "The walk-through's notes are not ones we can keep.";
  if (!walk || typeof walk !== "object" || Array.isArray(walk)) return bad;
  const w = walk as Record<string, unknown>;
  if (w.step !== undefined && !(WALK_STEP_KEYS as readonly unknown[]).includes(w.step)) return bad;
  if (w.looked !== undefined) {
    if (!Array.isArray(w.looked) || w.looked.some((s) => !(WALK_STEP_KEYS as readonly unknown[]).includes(s))) return bad;
  }
  if (size(walk as BoardWalk) > WALK_BYTES_MAX) return "The walk-through's notes are too long to keep.";
  return null;
}
